import {
  Box,
  Button,
  Divider,
  Input,
  Select,
  Textarea,
} from "@chakra-ui/react";
import { FloatingLayer } from "../utils/FloatingLayer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { THEME_COLOR_SCHEME } from "../utils/theme";
import { Activity } from "common";
import { useAPIQuery } from "../api/useAPIQuery";
import { useAPI } from "../api/useAPI";
import { useToastOnAPIError } from "../api/useToastOnAPIError";
import { useOnAPISuccess } from "../api/useOnAPISuccess";
import moment from "moment";
import { CALENDAR_BACKGROUND_COLORS, CALENDAR_TEXT_COLORS } from "./consts";

interface CreateActivityFloatingLayerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  editActivityId?: number | null;
  initialValues?: Partial<Activity>;
}

export const CreateActivityFloatingLayer = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  editActivityId,
  initialValues = {},
}: CreateActivityFloatingLayerProps) => {
  const api = useAPI();
  const [eventName, setEventName] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [background, setBackground] = useState("#81E6D9");
  const [textColor, setTextColor] = useState("#2D3748");

  const { data, state, error, query } = useAPIQuery<"activityPost">({
    apiCall: api.activityPost,
  });
  useToastOnAPIError({ title: "Unable to create activity", error });
  useOnAPISuccess<"activityPost">({
    data,
    state,
    onSuccess: () => {
      onSubmitSuccess?.();
      onClose();
    },
  });

  const isEditMode = useMemo(() => Boolean(editActivityId), [editActivityId]);

  useEffect(() => {
    if (editActivityId) {
      api.activityGet({ id: editActivityId }).then((response) => {
        const targetActivity = response.activities.find(
          (a) => a.id === editActivityId
        );
        if (!targetActivity) {
          return;
        }
        setEventName(targetActivity.title);
        setEventDescription(targetActivity.description);
        setStartTime(
          moment
            .unix(targetActivity.startTime / 1000)
            .format("YYYY-MM-DDTHH:mm")
        );
        setEndTime(
          moment.unix(targetActivity.endTime / 1000).format("YYYY-MM-DDTHH:mm")
        );
        setBackground(targetActivity.background);
        setTextColor(targetActivity.textColor);
      });
    }
  }, [api, editActivityId]);

  useEffect(() => {
    if (isOpen && !isEditMode) {
      setEventName(initialValues.title ?? "");
      setEventDescription(initialValues.description ?? "");

      setStartTime(
        initialValues.startTime
          ? moment
              .unix(initialValues.startTime / 1000)
              .format("YYYY-MM-DDTHH:mm")
          : ""
      );
      setEndTime(
        initialValues.endTime
          ? moment.unix(initialValues.endTime / 1000).format("YYYY-MM-DDTHH:mm")
          : ""
      );
    }
  }, [initialValues, isEditMode, isOpen]);

  const onCreateActivity = useCallback(() => {
    if (eventName.length === 0) {
      return;
    }
    if (eventDescription.length === 0) {
      return;
    }
    if (startTime.length === 0) {
      return;
    }
    if (endTime.length === 0) {
      return;
    }
    const activity: Omit<Activity, "id"> = {
      title: eventName,
      description: eventDescription,
      startTime: new Date(startTime).getTime(),
      endTime: new Date(endTime).getTime(),
      textColor,
      background,
    };
    query(editActivityId ? { id: editActivityId, ...activity } : activity);
  }, [
    background,
    editActivityId,
    endTime,
    eventDescription,
    eventName,
    query,
    startTime,
    textColor,
  ]);

  return (
    <FloatingLayer isOpen={isOpen} onClose={onClose}>
      <Box fontWeight={"bold"} fontSize={"1.5rem"}>
        {isEditMode ? "Edit Activity" : "Add a new activity"}
      </Box>
      <Divider marginTop={2} marginBottom={4} />
      <Input
        placeholder="Event Name"
        marginBottom={4}
        value={eventName}
        onChange={(e) => setEventName(e.target.value)}
      />
      <Textarea
        marginBottom={4}
        placeholder="Description"
        value={eventDescription}
        onChange={(e) => setEventDescription(e.target.value)}
        rows={10}
      />
      <Input
        marginBottom={4}
        value={startTime}
        onChange={(e) => setStartTime(e.target.value)}
        type="datetime-local"
      />
      <Input
        marginBottom={4}
        value={endTime}
        onChange={(e) => setEndTime(e.target.value)}
        type="datetime-local"
      />
      <Select
        marginBottom={4}
        placeholder="Background Color"
        value={background}
        onChange={(e) => setBackground(e.target.value)}
      >
        {CALENDAR_BACKGROUND_COLORS.map((color) => (
          <option key={color} value={color}>
            {color}
          </option>
        ))}
      </Select>
      <Select
        marginBottom={4}
        placeholder="Text Color"
        value={textColor}
        onChange={(e) => setTextColor(e.target.value)}
      >
        {CALENDAR_TEXT_COLORS.map((color) => (
          <option key={color} value={color}>
            {color}
          </option>
        ))}
      </Select>
      <Button
        isDisabled={
          eventName.length === 0 ||
          eventDescription.length === 0 ||
          startTime.length === 0 ||
          endTime.length === 0 ||
          background.length === 0 ||
          textColor.length === 0
        }
        width={"100%"}
        colorScheme={THEME_COLOR_SCHEME}
        onClick={onCreateActivity}
        size={"lg"}
      >
        {isEditMode ? "Update" : "Create"}
      </Button>
    </FloatingLayer>
  );
};
